import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Mail, Phone, Lock, ArrowRight } from 'lucide-react';
import { countryCodes } from '../../../utils/countryCodes';

export default function SignupForm({
  signupName, setSignupName, signupEmail, setSignupEmail,
  signupCountryCode, setSignupCountryCode, signupPhone, setSignupPhone,
  signupPassword, setSignupPassword, loading, onSubmit,
}) {
  return (
    <TabsContent value="signup">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
        <div>
          <Label htmlFor="signup-name" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Name
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-name"
              data-testid="signup-name-input"
              type="text"
              placeholder="John Doe"
              value={signupName}
              onChange={(e) => setSignupName(e.target.value)}
              className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="signup-email" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-email"
              data-testid="signup-email-input"
              type="email"
              placeholder="your@email.com"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="signup-phone" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Phone Number
          </Label>
          <div className="flex gap-2">
            <Select
              value={signupCountryCode}
              onValueChange={setSignupCountryCode}
            >
              <SelectTrigger
                data-testid="signup-country-code-select"
                className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary w-[140px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {/* Dedupe by dial code — Radix Select uses `value` as the
                    React key of an internal hidden <option>, so entries
                    sharing a dial code (e.g. +1 US/Canada, +7 RU/KZ)
                    would otherwise trigger duplicate-key warnings. */}
                {Array.from(new Map(countryCodes.map(c => [c.code, c])).values()).map((country) => (
                  <SelectItem key={country.code + country.country} value={country.code}>
                    <span className="flex items-center gap-2">
                      <span>{country.flag}</span>
                      <span>{country.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="signup-phone"
                data-testid="signup-phone-input"
                type="tel"
                placeholder="Enter phone number"
                value={signupPhone}
                onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, ''))}
                className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                required
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Enter phone number without country code
          </p>
        </div>
        <div>
          <Label htmlFor="signup-password" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-password"
              data-testid="signup-password-input"
              type="password"
              placeholder="••••••••"
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
              required
            />
          </div>
        </div>
        <Button
          type="submit"
          data-testid="signup-submit-button"
          className="w-full btn-volt h-14"
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </form>
    </TabsContent>
  );
}
